import { useEffect, useRef, useState } from 'react'
import HomeUnifiedHero, { buildHomeHeroMeta } from '../../../../src/components/home/HomeUnifiedHero'
import HomeMaintenanceBanner from '../../../../src/components/home/HomeMaintenanceBanner'
import RegistrationHeroCard from '../../../../src/components/home/RegistrationHeroCard'
import HomeHeroCarousel from '../../../../src/components/home/HomeHeroCarousel'
import { runHeroStateEngine } from '../../../../src/utils/heroStateEngine'
import { buildHeroCardData, getHeroMotionMode, isHeroMotionUrgent } from '../../../../src/utils/heroCardData'
import { getActiveAcademicHero } from '../../../../src/utils/academicHero'
import { isBeforeSemesterStart } from '../../../../src/utils/registrationHeroState'
import { formatSemesterWeek } from '../../../../src/utils/semesterUtils'
import { shouldShowHomeMaintenanceBanner } from '../../../../packages/display/cmsHome'
import { EVENING_DONE_HERO_COPY } from '../../../../src/utils/heroCardData'
import '../../../../src/App.css'
import './HomeHeroPreview.css'

export const HERO_PREVIEW_LOGICAL_WIDTH = 402
const HERO_PREVIEW_SCALES = [1, 1.25, 1.5]

// 관리자 미리보기 전용 시간표 fixture — 화요일 학생 기준 전형적인 수업 구성.
const PREVIEW_COURSES = [
  { name: '자료구조', room: 'IT관 301', startPeriod: 1, endPeriod: 1 },
  { name: '운영체제', room: 'IT관 205', startPeriod: 4, endPeriod: 4 },
  { name: '알고리즘', room: '공학관 401', startPeriod: 6, endPeriod: 6 },
]

const PREVIEW_SHUTTLE = {
  isWeekend: false,
  isEnded: false,
  isFavorite: true,
  routeName: '병점역 → 학교',
  nextTime: '09:10',
  firstTime: '06:30',
  lastTime: '22:00',
}

const PREVIEW_CAFETERIA = {
  title: '제육볶음 · 계란국',
  subtitle: '학생식당 운영 중',
  meta: null,
  available: true,
}

function getCanonicalPreviewCopy(hero) {
  const { mainMessage, infoLine, ctaLabel, mascotExpression } = buildHeroCardData(hero)
  return {
    statusLabel: mainMessage,
    subLine: infoLine,
    ctaLabel,
    mascotVariant: mascotExpression,
  }
}

// now 시각에 맞춘 셔틀 상태 계산
function getPreviewShuttle(now) {
  const h = now.getHours()
  const m = now.getMinutes()
  const nowMin = h * 60 + m
  const isWeekend = now.getDay() === 0 || now.getDay() === 6
  if (isWeekend) return { ...PREVIEW_SHUTTLE, isWeekend: true, isEnded: true, nextTime: null }

  const SHUTTLE_TIMES = [
    390, 420, 450, 480, 510, 535, 550, 565, 580, 595, 610, 625,
    730, 760, 800, 835, 870, 900, 945, 970, 1000, 1020, 1040, 1060,
    1080, 1100, 1120, 1140, 1160, 1180, 1200, 1220, 1240, 1260, 1280,
  ]
  const next = SHUTTLE_TIMES.find(t => t > nowMin)
  if (!next) return { ...PREVIEW_SHUTTLE, isEnded: true, nextTime: null }
  const hh = Math.floor(next / 60)
  const mm = next % 60
  const timeStr = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
  const direction = (h >= 6 && h < 11) ? '병점역 → 학교' : '학교 → 병점역'
  return { ...PREVIEW_SHUTTLE, nextTime: timeStr, routeName: direction, isWeekend: false, isEnded: false }
}

function computeHeroFromTime(now) {
  const isWeekend = now.getDay() === 0 || now.getDay() === 6
  const shuttle = getPreviewShuttle(now)
  const shuttleMinutesLeft = shuttle.nextTime
    ? Math.max(0, (parseInt(shuttle.nextTime.split(':')[0], 10) * 60 + parseInt(shuttle.nextTime.split(':')[1], 10)) - (now.getHours() * 60 + now.getMinutes()))
    : null
  const todayCourses = isWeekend ? [] : PREVIEW_COURSES
  const raw = runHeroStateEngine({
    now,
    uid: 'preview-admin',
    todayCourses,
    timetableExists: true,
    nextClassFuture: null,
    shuttle: shuttle.isEnded || shuttle.isWeekend ? null : shuttle,
    shuttleMinutesLeft,
    cafeteriaSummary: PREVIEW_CAFETERIA,
    hasCommuteSignal: true,
  })
  return raw
}

function HeroCard({ modeId, config, maintenancePreview, previewDate }) {
  if (maintenancePreview || shouldShowHomeMaintenanceBanner(config)) {
    return <HomeMaintenanceBanner message={config?.maintenanceMessage?.message || '홈 시스템 점검 중입니다.'} isDark={false} />
  }

  // 1. 강제 모드: 수강신청 / 개강 D-Day fixture
  if (modeId === 'registration' || modeId === 'registration:semester-start') {
    const REGISTRATION_FIXTURES = {
      registration: { phaseId: 'preview-registration', label: '예비수강신청까지', big: { mode: 'dday', value: 'D-12' }, caption: '9월 1일 시작', prompt: '준비한 시간표를 확인해보세요', metaLine: '2026-2학기 · 예비수강신청' },
      'registration:semester-start': { phaseId: 'preview-semester-start', label: '개강', big: { mode: 'dday', value: 'D-3' }, caption: '9월 1일 개강', prompt: '수강정정은 개강일 09:00에 시작해요', metaLine: '2026-2학기 · 개강' },
    }
    return <RegistrationHeroCard isDark={false} previewState={REGISTRATION_FIXTURES[modeId]} onClick={() => {}} />
  }

  // 2. 강제 모드: 기타 fixture 선택
  if (modeId != null) {
    const FIXTURES = {
      'before-class:early': { motionMode: 'class', statusLabel: '곧 수업이 시작돼요', subLine: '09:30 · 자료구조 · IT관 301', ctaLabel: '시간표 보기', mascotVariant: 'welcome' },
      'commute-to-school': { motionMode: 'shuttle', variant: 'data', statusLabel: '오늘도 등교할 시간이에요', subLine: '병점역 → 학교', shuttleTime: '09:10', minsToShuttle: 8, shuttleRouteName: '병점역 → 학교', ctaLabel: '셔틀 보기' },
      'before-class:first-class': { motionMode: 'class', statusLabel: '곧 수업이 시작돼요', subLine: '09:30 · 자료구조 · IT관 301 · 45분 뒤', ctaLabel: '시간표 보기', mascotVariant: 'looking' },
      'in-class': { motionMode: 'class', statusLabel: '지금은 수업 시간이에요', subLine: '자료구조 · IT관 301 · 40분 남음', ctaLabel: '시간표 보기', mascotVariant: 'looking' },
      'before-class:break': { motionMode: 'class', statusLabel: '지금은 쉬는 시간이에요', subLine: '다음 수업 15분 뒤 · 운영체제 · IT관 205', ctaLabel: '시간표 보기', mascotVariant: 'neutral' },
      'before-class:gap': { motionMode: 'class', statusLabel: '지금은 공강이에요', subLine: '다음 수업 2시간 뒤 · 운영체제 · IT관 205', ctaLabel: '시간표 보기', mascotVariant: 'sleepy' },
      lunch: { motionMode: 'meal', ...getCanonicalPreviewCopy({ type: 'meal' }) },
      'shuttle-info': { motionMode: 'shuttle', variant: 'data', statusLabel: '셔틀이 곧 출발해요', subLine: '병점역 → 학교', shuttleTime: '12:40', minsToShuttle: 12, shuttleRouteName: '병점역 → 학교', ctaLabel: '셔틀 보기' },
      'go-home': { motionMode: 'shuttle', variant: 'data', statusLabel: '이제 집에 갈 시간이에요', subLine: '학교 → 병점역', shuttleTime: '16:40', minsToShuttle: 8, shuttleRouteName: '학교 → 병점역', ctaLabel: '셔틀 보기', mascotVariant: 'success' },
      'day-done': { motionMode: 'planner', statusLabel: EVENING_DONE_HERO_COPY.mainMessage, subLine: EVENING_DONE_HERO_COPY.infoLine, ctaLabel: '시간표 보기', mascotVariant: 'success', nightSkyHero: true },
      'rest-day:weekday-no-class': { motionMode: 'planner', statusLabel: '오늘은 수업이 없어요', subLine: '여유로운 하루를 보내도 좋아요', ctaLabel: '시간표 보기', mascotVariant: 'sleepy' },
      'rest-day:weekend-no-class': { motionMode: 'weekend', statusLabel: '오늘은 쉬는 날이에요', subLine: '여유로운 주말을 보내세요', ctaLabel: '시간표 보기', mascotVariant: 'sleepy' },
      'empty-timetable': { motionMode: 'planner', ...getCanonicalPreviewCopy({ meta: '시간표 등록하기 ›' }) },
    }
    const fixture = FIXTURES[modeId] ?? FIXTURES['before-class:first-class']
    const now = previewDate || new Date()
    const DAY_MAP = ['일', '월', '화', '수', '목', '금', '토']
    const dateStr = `${now.getMonth() + 1}월 ${now.getDate()}일 (${DAY_MAP[now.getDay()]})`
    const weekStr = formatSemesterWeek(now)
    return (
      <HomeUnifiedHero
        isDark={false}
        metaLine={buildHomeHeroMeta({ dateStr, weekStr, profile: null, previewMode: true })}
        displayName="예성"
        onClick={() => {}}
        {...fixture}
      />
    )
  }

  // 3. 시간 기준 실시간 계산 (기본 모드)
  const now = previewDate || new Date()
  const DAY_MAP = ['일', '월', '화', '수', '목', '금', '토']
  const dateStr = `${now.getMonth() + 1}월 ${now.getDate()}일 (${DAY_MAP[now.getDay()]})`
  const weekStr = formatSemesterWeek(now)
  const heroRaw = computeHeroFromTime(now)
  const isNight = heroRaw?.context === 'evening-done' || (heroRaw?.type === 'default' && (now.getHours() >= 20 || now.getHours() < 6))
  const { variant, mainMessage, infoLine, ctaLabel, mascotExpression } = buildHeroCardData(heroRaw, isNight)
  const motionMode = getHeroMotionMode(heroRaw)
  const urgent = isHeroMotionUrgent(heroRaw)

  const standardHero = (
    <HomeUnifiedHero
      isDark={false}
      motionMode={motionMode}
      variant={variant}
      urgent={urgent}
      nightSkyHero={isNight}
      metaLine={buildHomeHeroMeta({ dateStr, weekStr, profile: null, previewMode: true })}
      displayName="예성"
      statusLabel={mainMessage}
      subLine={infoLine}
      ctaLabel={ctaLabel}
      mascotVariant={mascotExpression}
      onClick={() => {}}
      shuttleTime={heroRaw?.shuttleTime ?? null}
      minsToShuttle={heroRaw?.minsToShuttle ?? null}
      shuttleRouteName={heroRaw?.subtitle ?? null}
    />
  )

  const academicHero = getActiveAcademicHero({ now })
  return (
    <HomeHeroCarousel
      standardHero={standardHero}
      academicHero={academicHero && <RegistrationHeroCard isDark={false} now={now} previewState={academicHero.registrationState} onClick={() => {}} />}
      isPreSemester={isBeforeSemesterStart(now)}
    />
  )
}

export default function HomeHeroPreview({ config, modeId = null, maintenancePreview = false, previewDate = new Date() }) {
  const [scale, setScale] = useState(1.25)
  const viewportRef = useRef(null)

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    const hero = viewport.querySelector('.home-unified-hero, .registration-hero-card, .home-maintenance-banner')
    const computed = hero ? window.getComputedStyle(hero) : null
    console.info('[Hero QA Preview]', {
      mobileViewportWidth: viewport.clientWidth,
      computedHeroWidth: computed?.width ?? null,
    })
  }, [modeId, maintenancePreview, previewDate, scale])

  return (
    <section className="admin-home-hero-preview" aria-label="Hero QA 미리보기">
      <div className="admin-home-hero-preview__controls" aria-label="Preview 확대율">
        {HERO_PREVIEW_SCALES.map(option => (
          <button
            key={option}
            type="button"
            className={scale === option ? 'active' : ''}
            aria-pressed={scale === option}
            onClick={() => setScale(option)}
          >
            {Math.round(option * 100)}%
          </button>
        ))}
      </div>
      <div className="admin-home-hero-preview__scale-frame" style={{ width: `${HERO_PREVIEW_LOGICAL_WIDTH * scale}px`, '--hero-preview-scale': scale }}>
        <div
          ref={viewportRef}
          className="admin-home-hero-preview__screen admin-home-hero-preview__viewport"
          data-logical-width={HERO_PREVIEW_LOGICAL_WIDTH}
          style={{ width: `${HERO_PREVIEW_LOGICAL_WIDTH}px` }}
        >
          <div className="admin-home-hero-preview__production-shell">
            <HeroCard modeId={modeId} config={config} maintenancePreview={maintenancePreview} previewDate={previewDate} />
          </div>
        </div>
      </div>
    </section>
  )
}
